import type { CalEvent } from '../shared/types';
import { addDays } from './dates';
import { api } from './api';

const TZ = 'Europe/Berlin';
const API_BASE = 'https://www.googleapis.com/calendar/v3';
const SCOPE = 'https://www.googleapis.com/auth/calendar';
const GIS_URL = 'https://accounts.google.com/gsi/client';

interface TokenResponse {
  access_token?: string;
  error?: string;
  error_description?: string;
}
interface TokenClient {
  requestAccessToken(overrides?: { prompt?: string }): void;
}
interface GoogleAccounts {
  accounts: {
    oauth2: {
      initTokenClient(cfg: {
        client_id: string;
        scope: string;
        callback: (r: TokenResponse) => void;
        error_callback?: (e: { type: string }) => void;
      }): TokenClient;
    };
  };
}
declare global {
  interface Window {
    google?: GoogleAccounts;
  }
}

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** Lädt das Google-Identity-Services-Skript (vorab aufrufen, damit der Popup-Aufruf später sofort klappt). */
export function loadGis(): Promise<void> {
  if (window.google) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = GIS_URL;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Google-Anmeldeskript konnte nicht geladen werden.'));
    document.head.append(s);
  });
}

function requestToken(clientId: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const g = window.google;
    if (!g) {
      reject(new Error('Google-Anmeldung ist nicht geladen.'));
      return;
    }
    const client = g.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: SCOPE,
      callback: (r) =>
        r.access_token
          ? resolve(r.access_token)
          : reject(new Error(r.error_description ?? r.error ?? 'Anmeldung fehlgeschlagen.')),
      error_callback: (e) => reject(new Error(`Google-Anmeldung abgebrochen (${e.type}).`)),
    });
    client.requestAccessToken({ prompt: '' });
  });
}

async function gfetch(token: string, method: string, path: string, body?: unknown): Promise<Response> {
  for (let attempt = 0; ; attempt++) {
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if ((res.status === 429 || res.status === 403) && attempt < 4) {
      const text = await res.clone().text();
      if (res.status === 429 || /rateLimitExceeded|userRateLimitExceeded/.test(text)) {
        await sleep(800 * (attempt + 1));
        continue;
      }
    }
    return res;
  }
}

async function apiError(res: Response): Promise<Error> {
  const text = await res.text();
  return new Error(`Google-API ${res.status}: ${text.slice(0, 200)}`);
}

async function findOrCreateCalendar(token: string, calendarName: string): Promise<string> {
  let pageToken = '';
  for (;;) {
    const q = pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : '';
    const res = await gfetch(token, 'GET', `/users/me/calendarList?maxResults=250${q}`);
    if (!res.ok) throw await apiError(res);
    const data = (await res.json()) as {
      items?: { id: string; summary?: string; accessRole?: string }[];
      nextPageToken?: string;
    };
    const hit = data.items?.find((c) => c.summary === calendarName && c.accessRole === 'owner');
    if (hit) return hit.id;
    if (!data.nextPageToken) break;
    pageToken = data.nextPageToken;
  }
  const created = await gfetch(token, 'POST', '/calendars', { summary: calendarName, timeZone: TZ });
  if (!created.ok) throw await apiError(created);
  return ((await created.json()) as { id: string }).id;
}

/** Google-Event-IDs dürfen nur a–v und 0–9 enthalten: UUID ohne Bindestriche (Hex) passt. */
const gid = (id: string): string => `fk${id.replace(/-/g, '')}`;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Google kennt nur 11 feste Ereignisfarben (colorId). Beste Entsprechung zur Kalenderansicht:
 * 6 = Mandarine (orange, offen), 2 = Salbei (hellgrün, erledigt), 7 = Pfau (blau, ICS-Termin),
 * 8 = Graphit (grau, Individualtermin).
 */
function googleColorId(e: CalEvent): string {
  if (e.source === 'ics') return '7';
  if (e.individual) return '8';
  return e.doneBy ? '2' : '6';
}

function toGoogleEvent(e: CalEvent): Record<string, unknown> {
  const base: Record<string, unknown> = {
    // wie in der Kalenderansicht: <Termin-Name> (<Erlediger>), nie bei ICS-Terminen
    summary: e.doneBy && e.source !== 'ics' ? `${e.title} (${e.doneBy})` : e.title,
    description: e.description ?? '',
    status: 'confirmed',
    colorId: googleColorId(e),
  };
  if (e.time) {
    const [y, mo, d] = e.date.split('-').map(Number);
    const [hh, mm] = e.time.split(':').map(Number);
    const end = new Date(y, mo - 1, d, hh + 1, mm);
    const endStr = `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}T${pad(end.getHours())}:${pad(end.getMinutes())}:00`;
    base.start = { dateTime: `${e.date}T${e.time}:00`, timeZone: TZ };
    base.end = { dateTime: endStr, timeZone: TZ };
  } else {
    base.start = { date: e.date };
    base.end = { date: addDays(e.date, 1) };
  }
  return base;
}

function fingerprint(s: string): string {
  let hash = 5381;
  for (let i = 0; i < s.length; i++) hash = ((hash << 5) + hash + s.charCodeAt(i)) | 0;
  return String(hash >>> 0);
}

function loadFingerprints(key: string): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(key) ?? '{}') as Record<string, string>;
  } catch {
    return {};
  }
}
function saveFingerprints(key: string, map: Record<string, string>): void {
  try {
    localStorage.setItem(key, JSON.stringify(map));
  } catch {
    /* nicht kritisch */
  }
}

export interface SyncResult {
  synced: number;
  unchanged: number;
  deleted: number;
  failed: number;
}

/**
 * Gleicht alle Termine mit dem Google-Kalender `calendarName` ab:
 * neue/geänderte Termine werden angelegt bzw. aktualisiert, gelöschte entfernt.
 * Muss direkt aus einem Klick-Handler gestartet werden (Popup der Google-Anmeldung).
 */
export async function syncToGoogle(
  clientId: string,
  calendarName: string,
  events: CalEvent[],
  force: boolean,
  log: (msg: string) => void,
): Promise<SyncResult> {
  const tokenPromise = requestToken(clientId); // sofort starten (Nutzer-Geste)
  const token = await tokenPromise;
  const tombstones = await api.tombstones();

  log('Suche Kalender …');
  const calId = await findOrCreateCalendar(token, calendarName);
  const calPath = `/calendars/${encodeURIComponent(calId)}/events`;
  const fpKey = `fk-gexport-${calId}`;
  const fps = force ? {} : loadFingerprints(fpKey);
  const result: SyncResult = { synced: 0, unchanged: 0, deleted: 0, failed: 0 };

  let i = 0;
  for (const e of events) {
    i++;
    log(`Übertrage Termin ${i} von ${events.length} …`);
    const body = toGoogleEvent(e);
    const fp = fingerprint(JSON.stringify(body));
    if (fps[e.id] === fp) {
      result.unchanged++;
      continue;
    }
    const id = gid(e.id);
    let res = await gfetch(token, 'PUT', `${calPath}/${id}`, body);
    if (res.status === 404) {
      res = await gfetch(token, 'POST', calPath, { ...body, id });
      if (res.status === 409) res = await gfetch(token, 'PUT', `${calPath}/${id}`, body);
    }
    if (res.ok) {
      fps[e.id] = fp;
      result.synced++;
    } else {
      result.failed++;
      console.error('Google-Export fehlgeschlagen', e.title, await res.text());
    }
  }

  for (const t of tombstones) {
    log('Entferne gelöschte Termine …');
    const res = await gfetch(token, 'DELETE', `${calPath}/${gid(t)}`);
    if (res.ok || res.status === 404 || res.status === 410) {
      delete fps[t];
      result.deleted++;
    } else {
      result.failed++;
    }
  }

  saveFingerprints(fpKey, fps);
  if (result.failed === 0 && tombstones.length > 0) await api.clearTombstones();
  return result;
}
